import React from "react";
import { Button, Grid, Typography } from "@mui/material";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-mui";
import { useTranslation } from "react-i18next";

import { APIError, RegularGrid } from "src/types";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { minMaxToString } from "src/utils/string.utils";
import { RegularGridMutationData } from "src/hooks/grids";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { adornments } from "src/config/adornments";
import { useRegularGridSchema } from "src/schemas/regular-grid.schema";

interface Props {
  data: RegularGrid;
  isLoading: boolean;
  errors?: APIError[] | null;
  readOnly?: boolean;
  onClose: () => void;
  onSubmit: (values: RegularGridMutationData) => void;
}

export function RegularGridForm(props: Props) {
  const { t } = useTranslation();
  const validationSchema = useRegularGridSchema(props.data);

  const initialValues: RegularGridMutationData = {
    xcount: props.data.xcount.value,
    ycount: props.data.ycount.value,
    distance: props.data.distance.value,
  };

  return render();

  function render() {
    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values: RegularGridMutationData) => {
          return props.onSubmit(values);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography>{t("label.regularGrid")}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="xcount"
                  label={t("label.numberOfPointsInXDirection")}
                  helperText={minMaxToString(props.data.xcount.minMaxDefault)}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="ycount"
                  label={t("label.numberOfPointsInYDirection")}
                  helperText={minMaxToString(props.data.ycount.minMaxDefault)}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="distance"
                  label={t("label.distanceBetweenPoints")}
                  helperText={minMaxToString(props.data.distance.minMaxDefault)}
                  InputProps={adornments(t).meter}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              {props.errors && (
                <Grid item xs={12}>
                  <ServerErrors data={props.errors} />
                </Grid>
              )}

              {!props.readOnly && (
                <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                  <Button variant="text" onClick={props.onClose}>
                    {t("label.cancel")}
                  </Button>
                  <ButtonLoading
                    isLoading={isSubmitting || props.isLoading}
                    type="submit"
                    color="primary"
                    variant="contained"
                  >
                    {t("label.save")}
                  </ButtonLoading>
                </ButtonGrid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }
}
